import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { Button } from '../../components/ui/Button';
import SafeIcon from '../../common/SafeIcon';
import { motion } from 'framer-motion';

export default function Cart() {
  const { cart, removeFromCart, checkout, user } = useAppContext();
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  
  const total = cart.reduce((sum, item) => sum + Number(item.price), 0);

  const handleCheckout = async () => {
    if (!user) {
      alert("Please log in to checkout.");
      navigate('/login');
      return;
    }

    setIsProcessing(true);
    const success = await checkout();
    setIsProcessing(false);

    if (success) {
      alert("Order placed successfully!");
      navigate('/dashboard/orders');
    } else {
      alert("There was an error processing your order. Please try again.");
    }
  };

  if (cart.length === 0) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
        <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 mb-6">
          <SafeIcon name="ShoppingCart" className="text-4xl" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Your cart is empty</h2>
        <p className="text-gray-500 mb-8">Looks like you haven't added any TapBadges yet.</p>
        <Link to="/store">
          <Button size="lg">Continue Shopping</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="py-12 bg-gray-50 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Shopping Cart</h1>
        <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
          <ul className="divide-y divide-gray-100">
            {cart.map((item) => (
              <motion.li 
                key={item.cartId} 
                layout 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                exit={{ opacity: 0 }} 
                className="p-6 flex flex-col sm:flex-row gap-6 items-center"
              >
                <img src={item.image_url} alt={item.name} className="w-24 h-24 rounded-xl object-cover bg-gray-100" />
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="text-lg font-bold text-gray-900">{item.name}</h3>
                  <p className="text-sm text-gray-500 mt-1">Color: {item.options.color}</p>
                  {item.options.customText && <p className="text-sm text-gray-500">Print: "{item.options.customText}"</p>}
                </div>
                <div className="flex items-center gap-6">
                  <span className="text-lg font-bold text-gray-900">${item.price}</span>
                  <button 
                    onClick={() => removeFromCart(item.cartId)}
                    className="text-gray-400 hover:text-red-500 transition-colors p-2"
                  >
                    <SafeIcon name="Trash2" />
                  </button>
                </div>
              </motion.li>
            ))}
          </ul>
          <div className="bg-gray-50 p-6 sm:p-8 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-6">
            <div>
              <p className="text-sm text-gray-500 mb-1">Total due</p>
              <p className="text-3xl font-extrabold text-gray-900">${total.toFixed(2)}</p>
            </div>
            <Button 
              size="lg" 
              className="w-full sm:w-auto px-12 py-4 rounded-2xl text-lg"
              onClick={handleCheckout}
              disabled={isProcessing}
            >
              {isProcessing ? 'Processing...' : 'Checkout Securely'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
